import {
  eachDayOfInterval,
  format,
  formatISO,
  getDay,
  parseISO,
  startOfMonth,
} from 'date-fns';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';

export const formatTimestampToReadableString = (time: number) => {
  const seconds = Math.floor((time / 1000) % 60);
  const minutes = Math.floor((time / (1000 * 60)) % 60);
  const hours = Math.floor((time / (1000 * 60 * 60)) % 24);

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Note: rrule library is not currently correctly splitting multiple strings because of unfixed long-sitting bugs in parsing DTSART
// e.g. https://github.com/jkbrzt/rrule/issues/413 & https://github.com/jkbrzt/rrule/issues/332 & https://github.com/jkbrzt/rrule/issues/587
// So this needs to be removed once those are fixed

export const splitRruleString = (rruleString: string) => {
  const rruleLines: string[] = rruleString.split('\n');

  // Note: rrule library is not currently correctly processing strings with EXDATE, multiple lines, so this is a workaround
  const exdates = rruleLines
    .find((line) => line.startsWith('EXDATE:'))
    ?.split(':')?.[1]
    ?.split(',')
    ?.map((d) => parseISO(d).toISOString());
  const rruleLinesWithoutExdates = rruleLines.filter(
    (line) => !line.startsWith('EXDATE:'),
  );

  const dtStartIndices: number[] = rruleLinesWithoutExdates.reduce(
    (indices: number[], line: string, index: number) =>
      line.startsWith('DTSTART:') ? [...indices, index] : indices,
    [],
  );

  const arrayOfStringsFromString = dtStartIndices.map(
    (startIndex: number, index: number) => {
      const endIndex: number =
        dtStartIndices[index + 1] || rruleLinesWithoutExdates.length;
      return rruleLinesWithoutExdates.slice(startIndex, endIndex).join('\n');
    },
  );

  return { arrayOfStringsFromString, exdates };
};

export const getRruleStringAndExdates = (rruleString: string) => {
  const rruleLines: string[] = rruleString.split('\n');

  // Note: rrule library is not currently correctly processing strings with EXDATE, multiple lines, so this is a workaround
  const exdates = rruleLines
    .find((line) => line.startsWith('EXDATE:'))
    ?.split(':')?.[1]
    ?.split(',')
    ?.map((d) => parseISO(d).toISOString());
  const rruleWithoutExdates = rruleLines
    .filter((line) => !line.startsWith('EXDATE:'))
    .join('\n');

  return { rruleWithoutExdates, exdates };
};

export const formatDateForRrule = (d: string) => {
  const utcDate = parseISO(d);
  const zonedDate = utcToZonedTime(utcDate, 'Etc/UTC');
  return format(zonedDate, "yyyyMMdd'T'HHmmss'Z'");
};

export const getNthOccurrenceOfDayInMonth = (date: Date) => {
  const dayOfWeek = getDay(date);
  const firstDayOfMonth = startOfMonth(date);
  let occurrence = 0;

  const days = eachDayOfInterval({
    start: firstDayOfMonth,
    end: date,
  });

  for (const day of days) {
    if (getDay(day) === dayOfWeek) {
      occurrence++;
    }
  }

  return occurrence;
};
